import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TableRow, TableCell, Tooltip } from '@material-ui/core';
import moment from 'moment';

import Img from 'retired/Img';
import EditIcon from 'assets/icons/edit.svg';
import { primaryBlue } from 'utils/color-styles';
import { NonprofitNomination, NonprofitNominationStatusType } from 'generated/graphql';

import InternalNoteModal from './InternalNoteModal';
import StatusModal from './StatusModal';
import NonprofitNominationStatusChip from './NonprofitNominationStatusChip';
import OutreachModal from './OutreachModal';
import { getLastOutreachDate } from './getLastOutreachDate';

const NonprofitNominationsRow = ({
  nomination,
  hasGlobalHandleNonprofitNominationsPrivilege,
}: {
  nomination: NonprofitNomination;
  hasGlobalHandleNonprofitNominationsPrivilege: boolean;
}): JSX.Element => {
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [outreachModalOpen, setOutreachModalOpen] = useState(false);
  const [updatedNominationStatus, setUpdatedNominationStatus] = useState(nomination.status);

  const displayDate = (date?: Date | null): string =>
    date ? moment(date).format('MMM DD, YYYY') : '-';

  const localeCode = new Intl.DisplayNames(['en'], {
    type: 'region',
  });

  const handleOpenStatusModal = (value: NonprofitNominationStatusType): void => {
    if (value === NonprofitNominationStatusType.Outreach) {
      setOutreachModalOpen(true);
    } else {
      setUpdatedNominationStatus(value);
      setStatusModalOpen(true);
    }
  };

  const nonprofitEntityTypeLabel: string = nomination.nonprofitEntityType
    ? {
        nonprofit: 'Nonprofit',
        governmentEntity: 'Government Entity',
        school: 'School',
        memberAssociatedEntity: '-',
      }[nomination.nonprofitEntityType]
    : '-';

  const lastOutreachDate = getLastOutreachDate(nomination);

  return (
    <TableRow>
      {noteModalOpen && (
        <InternalNoteModal
          handleCloseNoteModal={(): void => setNoteModalOpen(false)}
          nomination={nomination}
        />
      )}
      {statusModalOpen && (
        <StatusModal
          handleCloseStatusModal={() => setStatusModalOpen(false)}
          updatedNominationStatus={updatedNominationStatus}
          nominationId={nomination.id}
        />
      )}
      {outreachModalOpen && (
        <OutreachModal onClose={() => setOutreachModalOpen(false)} nomination={nomination} />
      )}
      <TableCell
        size="medium"
        style={{ position: 'sticky', zIndex: 1, left: 0, backgroundColor: 'white' }}
      >
        {nomination.nonprofitName}
      </TableCell>
      <TableCell size="medium">{nonprofitEntityTypeLabel}</TableCell>
      <TableCell size="medium">{localeCode.of(nomination.country.substring(0, 2))}</TableCell>
      <TableCell size="medium">{nomination.engagementType}</TableCell>
      <Tooltip title={nomination.otherNote || ''}>
        <TableCell
          size="medium"
          style={{
            maxWidth: 200,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {nomination.otherNote || '—'}
        </TableCell>
      </Tooltip>
      <TableCell size="medium">{nomination.email || '—'}</TableCell>
      <TableCell size="medium">{nomination.ein || '—'}</TableCell>
      <TableCell size="medium">{nomination.ncesIdentifier || '—'}</TableCell>
      <TableCell size="medium">{nomination.phone || '—'}</TableCell>
      <TableCell size="medium" style={{ whiteSpace: 'nowrap' }}>
        {displayDate(nomination.createdAt)}
      </TableCell>
      <TableCell size="medium" style={{ whiteSpace: 'nowrap' }}>
        {displayDate(lastOutreachDate)}
      </TableCell>
      <TableCell
        size="medium"
        style={{
          color: primaryBlue,
        }}
      >
        {nomination?.User?.fullName?.first ? (
          <Link to={`/volunteers/${nomination.userId}`}>
            {nomination?.User?.fullName?.first} {nomination?.User?.fullName?.last}
          </Link>
        ) : (
          '-'
        )}
      </TableCell>
      {hasGlobalHandleNonprofitNominationsPrivilege && (
        <TableCell size="medium">{nomination.Company.name}</TableCell>
      )}
      <TableCell size="medium">
        <NonprofitNominationStatusChip
          nomination={nomination}
          handleOpenStatusModal={handleOpenStatusModal}
          hasGlobalHandleNonprofitNominationsPrivilege={
            hasGlobalHandleNonprofitNominationsPrivilege
          }
        />
      </TableCell>
      {hasGlobalHandleNonprofitNominationsPrivilege && (
        <TableCell>
          <div
            onClick={(): void => setNoteModalOpen(true)}
            style={{
              width: 175,
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            aria-hidden="true"
          >
            <Tooltip title={nomination?.internalNote || ''}>
              <div
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  marginRight: 10,
                }}
              >
                {nomination?.internalNote}
              </div>
            </Tooltip>
            <Img src={EditIcon} alt="edit" />
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};

export default NonprofitNominationsRow;
